// client/src/pages/HomePage.js
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../components/common/SEO";
import SearchBar from "../components/common/SearchBar";
import StickySearchWrapper from "../components/common/StickySearchWrapper";
import FullScreenFilterModal from "../components/business/FullScreenFilterModal";
import FilterPanel from "../components/business/FilterPanel";
import BusinessList from "../components/business/BusinessList";
import "../styles/home.css";
import "../styles/components-core.css"; // Business cards, search bar, header, footer
import HeroSection from "../components/common/HeroSection";
import { STICKY_SCROLL_THRESHOLD } from "../constants/ui";
import { getActiveFilterCount } from "../utils/filterUtils";

const HomePage = () => {
    const { t, i18n } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Memoized helper function to update URL with current state - prevents unnecessary re-renders
    const updateUrl = useCallback((newSearchTerm, newFilters) => {
        const urlParams = new URLSearchParams();

        // Add search term
        if (newSearchTerm) {
            urlParams.set("search", newSearchTerm);
        }

        // Add filters
        if (newFilters.categories?.length) {
            urlParams.set("categories", newFilters.categories.join(","));
        }
        if (newFilters.cities?.length) {
            urlParams.set("cities", newFilters.cities.join(","));
        }
        if (newFilters.businessTypes?.length) {
            urlParams.set("businessTypes", newFilters.businessTypes.join(","));
        }
        // Verified can be 'true' or 'false' (string). Only set when explicitly provided.
        if (typeof newFilters.verified !== 'undefined' && newFilters.verified !== '') {
            const v = newFilters.verified === true ? 'true' : newFilters.verified === false ? 'false' : String(newFilters.verified);
            urlParams.set("verified", v);
        }

        // Update URL without triggering a page reload
        const newUrl = urlParams.toString() ? `/?${urlParams.toString()}` : "/";
        navigate(newUrl, { replace: true });
    }, [navigate]);

    // Initialize state from URL parameters on component mount
    useEffect(() => {
        // Helper function to parse URL parameters into state
        const parseUrlParams = () => {
            const urlParams = new URLSearchParams(location.search);
            const parsedFilters = {};
            const parsedSearchTerm = urlParams.get("search") || "";

            // Parse categories
            const categories = urlParams.get("categories");
            if (categories) {
                parsedFilters.categories = categories.split(",");
            }

            // Parse cities
            const cities = urlParams.get("cities");
            if (cities) {
                parsedFilters.cities = cities.split(",");
            }

            // Parse business types
            const businessTypes = urlParams.get("businessTypes");
            if (businessTypes) {
                parsedFilters.businessTypes = businessTypes.split(",");
            }

            // Parse verified filter (support both 'true' and 'false')
            const verified = urlParams.get("verified");
            if (verified === "true" || verified === "false") {
                parsedFilters.verified = verified; // keep as string for consistency
            }

            return { parsedFilters, parsedSearchTerm };
        };

        const { parsedFilters, parsedSearchTerm } = parseUrlParams();
        setSearchTerm(parsedSearchTerm);
        setFilters(parsedFilters);
    }, [location.search]);

    // Check screen size
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Handle sticky behavior and hero fade
    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const shouldBeSticky = scrollY > STICKY_SCROLL_THRESHOLD;

            if (shouldBeSticky !== isSticky) {
                setIsSticky(shouldBeSticky);

                // Add/remove fade class to hero section
                const heroSection = document.querySelector(".hero-section");
                if (heroSection) {
                    if (shouldBeSticky) {
                        heroSection.classList.add("fade-out");
                    } else {
                        heroSection.classList.remove("fade-out");
                    }
                }
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isSticky]);

    // Reset search and filters when navigating to home page
    useEffect(() => {
        if (location.state?.resetSearch) {
            setSearchTerm("");
            setFilters({});
        }
    }, [location]);

    // Handle filter changes
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        updateUrl(searchTerm, newFilters);

        if (process.env.NODE_ENV === "development") {
            console.log("Filter changed:", newFilters);
        }
    };

    // Handle search term changes - memoized to prevent unnecessary re-renders
    const handleSearchChange = useCallback((newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        updateUrl(newSearchTerm, filters);
    }, [filters, updateUrl]);

    // Function to reset search and filters - memoized to prevent unnecessary re-renders
    const resetToMainPage = useCallback(() => {
        setSearchTerm("");
        setFilters({});
        updateUrl("", {});
    }, [updateUrl]);

    // Count active filters
    const activeFilters = getActiveFilterCount(filters);

    // Handle filter modal
    const handleFilterToggle = () => {
        setIsFilterModalOpen(true);
    };

    const handleFilterModalClose = () => {
        setIsFilterModalOpen(false);
    };

    const handleApplyFilters = () => {
        // Filters are already applied through handleFilterChange
        // This is just for closing the modal
    };

    // Send mobile search data to Header
    useEffect(() => {
        const mobileSearchData = {
            searchTerm,
            onSearch: handleSearchChange,
            onReset: resetToMainPage,
            onFilterToggle: handleFilterToggle,
            activeFilterCount: activeFilters,
            enabled: true // Enable mobile search on HomePage
        };

        // Dispatch custom event to Header
        window.dispatchEvent(
            new CustomEvent("update-mobile-search", {
                detail: mobileSearchData
            })
        );

        // Cleanup: disable mobile search when component unmounts
        return () => {
            window.dispatchEvent(
                new CustomEvent("update-mobile-search", {
                    detail: { ...mobileSearchData, enabled: false }
                })
            );
        };
    }, [searchTerm, filters, handleSearchChange, resetToMainPage, activeFilters]); // Update when search or filters change

    return (
        <div className="home-page">
            <SEO
                title={"TELO — Renovation Directory"}
                description={t('homePage.heroDescription') || "Find renovation companies, electricians, cleaners and more across Georgia."}
                canonical={window.location.origin + "/"}
                lang={i18n?.language || 'en'}
                robots={"index,follow,noimageindex"}
                og={{ type: 'website' }}
                twitter={{ card: 'summary_large_image' }}
            />
            {/* Sticky Search Wrapper */}
            <StickySearchWrapper
                searchTerm={searchTerm}
                onSearch={handleSearchChange}
                onReset={resetToMainPage}
                onFilterToggle={handleFilterToggle}
                activeFilterCount={activeFilters}
            />

            {/* Full-Screen Filter Modal */}
            <FullScreenFilterModal
                isOpen={isFilterModalOpen}
                onClose={handleFilterModalClose}
                filters={filters}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
            />

            <HeroSection
                title={t('homePage.heroTitle')}
                description={t('homePage.heroDescription')}
                backgroundType="construction"
                overlayType="dark"
            >
                <SearchBar
                    onSearch={handleSearchChange}
                    searchTerm={searchTerm}
                    onReset={resetToMainPage}
                />
            </HeroSection>

            <div className="container">
                <div className="content-section">
                    {/* Desktop Sidebar Filter */}
                    {!isMobile && (
                        <div className="sidebar">
                            <FilterPanel
                                filters={filters}
                                onFilterChange={handleFilterChange}
                            />
                        </div>
                    )}

                    {/* Main Content */}
                    <div className="results-content">
                        <BusinessList
                            searchTerm={searchTerm}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;

